import React from 'react';
import PropTypes from 'prop-types';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { VariableNode } from './VariableExtension';
import Toolbar from './Toolbar';
import TableToolbar from './TableToolbar';

const TiptapHtmlEditor = ({ value, onChange, disabled, variables, rows = 8 }) => {
  const lastHtmlRef = React.useRef(value || '');
  const [, forceTick] = React.useReducer((x) => x + 1, 0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        // Link ships with StarterKit v3; the defaults are tuned for reading,
        // not editing — a click would navigate away instead of placing the caret.
        link: {
          openOnClick: false,
          enableClickSelection: true,
          autolink: true,
          linkOnPaste: true,
          defaultProtocol: 'https',
          protocols: ['mailto', 'tel'],
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph', 'blockquote', 'listItem'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      VariableNode,
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastHtmlRef.current = html;
      if (html !== value) onChange && onChange(html);
    },
  });

  React.useEffect(() => {
    if (editor && value !== lastHtmlRef.current) {
      lastHtmlRef.current = value || '';
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  React.useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  React.useEffect(() => {
    if (!editor) return undefined;
    editor.on('selectionUpdate', forceTick);
    editor.on('transaction', forceTick);
    if (editor && !disabled) editor.commands.focus();
    return () => {
      editor.off('selectionUpdate', forceTick);
      editor.off('transaction', forceTick);
    };
  }, [editor]);

  const focusEditor = () => {
    if (editor && !disabled) editor.commands.focus();
  };

  return (
    <div
      className="cro-tiptap-editor"
      style={{ minHeight: rows * 30 }}
      onClick={focusEditor}
    >
      {!disabled && <Toolbar editor={editor} variables={variables} />}
      {!disabled && editor && editor.isActive('table') && <TableToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
};

TiptapHtmlEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  variables: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
  })),
  rows: PropTypes.number,
};

export default TiptapHtmlEditor;
